import { Link } from 'react-router-dom';
import { getRepo, type Article } from '../../lib/repo';
import { useAsync } from '../../lib/hooks';
import { ArticleCard } from '../../components/ArticleCard';
import { Seo } from '../../components/Seo';
import { Card, EmptyState, Spinner } from '../../components/ui';

export type StaticKind = 'latest' | 'trending' | 'about' | 'contact' | 'advertise' | 'terms' | 'privacy';

const COPY: Record<string, { title: string; body: string[] }> = {
  about: {
    title: 'About The Story Line',
    body: [
      'The Story Line is a literary, art and entertainment publication. We publish essays, reviews, interviews and culture reporting — told well.',
      'We are independent and reader-supported. Corrections are published promptly and attribution is always preserved.',
    ],
  },
  contact: {
    title: 'Contact',
    body: [
      'Editorial: editorial@thestoryline.xyz',
      'Advertising: ads@thestoryline.xyz',
      'Corrections and rights: corrections@thestoryline.xyz',
      'We aim to reply within two working days.',
    ],
  },
  advertise: {
    title: 'Advertise with us',
    body: [
      'We offer clearly-labelled display placements and sponsored features. Sponsored content is always disclosed and never presented as independent editorial.',
      'Write to ads@thestoryline.xyz with your brief and we will send the current rate card.',
    ],
  },
  terms: {
    title: 'Terms of use',
    body: [
      'By using this site you agree to use it lawfully and not to reproduce our content without permission.',
      'Article text and images remain the property of their respective rights holders.',
    ],
  },
  privacy: {
    title: 'Privacy policy',
    body: [
      'We collect the minimum needed to run the publication: anonymous page analytics and, if you subscribe, your email address.',
      'We do not sell personal data. Newsletter subscriptions can be cancelled at any time.',
    ],
  },
};

function Listing({ kind }: { kind: 'latest' | 'trending' }) {
  const { data, loading } = useAsync<Article[]>(() => getRepo().listPublicArticles({ limit: 40 }), []);
  const articles = data ?? [];
  const ordered = kind === 'trending' ? [...articles].sort((a, b) => b.viewCount - a.viewCount) : articles;

  return (
    <>
      <Seo name={kind} />
      <div className="mx-auto max-w-6xl px-4 py-10">
        <h1 className="font-display text-3xl font-semibold">{kind === 'latest' ? 'Latest' : 'Trending'}</h1>
        <p className="mt-2 text-muted">
          {kind === 'latest' ? 'Everything we have published, newest first.' : 'What readers are reading most right now.'}
        </p>
        <div className="mt-8">
          {loading ? <Spinner /> : null}
          {!loading && ordered.length === 0 ? <EmptyState title="Nothing published yet" /> : null}
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {ordered.map((a) => <ArticleCard key={a.id} article={a} />)}
          </div>
        </div>
      </div>
    </>
  );
}

export function StaticPages({ kind }: { kind: StaticKind }) {
  if (kind === 'latest' || kind === 'trending') return <Listing kind={kind} />;
  const copy = COPY[kind];
  if (!copy) return null;
  return (
    <>
      <Seo name={kind} />
      <div className="mx-auto max-w-2xl px-4 py-14">
        <h1 className="font-display text-3xl font-semibold">{copy.title}</h1>
        <div className="mt-6 space-y-4 text-muted">
          {copy.body.map((p) => <p key={p}>{p}</p>)}
        </div>
        <Card className="mt-10 p-5 text-sm">
          Looking for something specific? <Link to="/search" className="text-primary underline">Search the archive</Link> or
          browse <Link to="/latest" className="text-primary underline">the latest</Link>.
        </Card>
      </div>
    </>
  );
}
